import { I18n } from "react-redux-i18n";
import React from "react";
import isNil from "lodash/isNil";

import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { HttpErrorDto } from "@common/dto/http";
import { CreateFormatFolderParams } from "@common/actions/formats";

import {
  QueryManagementFormat,
  QueryManagementProps,
} from "./query-management.types";
import { FormatFieldItem } from "../format-field-selector";
import { FolderContext } from "@common/dto/folder/context";
import { searchQueryFormats } from "@modules/invite-status/services/formats";

export const useQueryManagementController = (props: QueryManagementProps) => {
  const {
    showErrorMessage,
    showInfoMessage,
    getFolders,
    getFormats,
    createFormatFolder,
    deleteFormatFolder,
    deleteFormat,
    onSelectedFolderChange,
    onSelectedFormatChange,
  } = props;

  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);
  const [isFormatsLoading, setIsFormatsLoading] = React.useState(false);

  const [searchKeyword, setSearchKeyword] = React.useState("");

  const [selectedFolder, setSelectedFolder] = React.useState<
    FolderPrimaryListDto | undefined
  >();
  const [selectedFormat, setSelectedFormat] = React.useState<
    QueryManagementFormat | undefined
  >();

  const [folders, setFolders] = React.useState<FolderPrimaryListDto[]>([]);
  const [formats, setFormats] = React.useState<QueryManagementFormat[]>([]);
  const [
    formatFolderCreatingDialogOpened,
    setFormatFolderCreatingDialogOpened,
  ] = React.useState(false);

  const handleGetFolders = React.useCallback(async () => {
    const params = { context: FolderContext.inviteQueryFolder };
    try {
      setIsFoldersLoading(true);
      const folders = await getFolders(params);
      setFolders(folders);
    } catch (err) {
      console.error(err);

      const errorMessage =
        (err as HttpErrorDto)?.entity ||
        I18n.t("Failed to get query management folders");
      showErrorMessage(errorMessage);
    } finally {
      setIsFoldersLoading(false);
    }
  }, [showErrorMessage, getFolders]);

  const isFormatIdEmpty =
    isNil(selectedFormat?.id) || selectedFormat?.id === "";
  const isFolderIdEmpty =
    isNil(selectedFolder?.id) || selectedFolder?.id.toString() === "";

  const handleGetFormats = React.useCallback(
    async (params: {
      searchKeyword?: string;
      folder?: FolderPrimaryListDto;
      context?: FolderContext.inviteQueryFolder;
    }) => {
      try {
        setIsFormatsLoading(true);
        const formats = await getFormats(params);
        setFormats(formats);
      } catch (err) {
        console.error(err);

        const errorMessage =
          (err as HttpErrorDto)?.entity ||
          I18n.t("Failed to get query management formats");
        showErrorMessage(errorMessage);
      } finally {
        setIsFormatsLoading(false);
      }
    },
    [showErrorMessage, getFormats]
  );

  const handleSearchFormate = async ({
    searchKeyword,
  }: {
    searchKeyword: string;
  }) => {
    try {
      setIsFormatsLoading(true);

      const formats = await searchQueryFormats({
        searchKeyword,
      });

      setFormats(formats);
    } catch (err) {
      console.error(err);
      const errorMessage =
        (err as HttpErrorDto)?.entity ||
        I18n.t("Failed to search query formats");
      showErrorMessage(errorMessage);
    } finally {
      setIsFormatsLoading(false);
    }
  };

  React.useEffect(() => {
    handleGetFolders();
  }, []);

  const handleSelectedFormatChange = React.useCallback(
    (format: FormatFieldItem) => {
      setSearchKeyword(format.name);
      setSelectedFormat(format as QueryManagementFormat);
      if (onSelectedFormatChange) {
        onSelectedFormatChange({
          format: { ...format, folderId: selectedFolder?.id?.toString() },
        });
      }
    },
    [onSelectedFormatChange, selectedFolder]
  );

  const handleSearchChange = React.useCallback(
    (searchKeyword: string) => {
      const format = {
        ...selectedFormat,
        name: searchKeyword,
      };

      handleSelectedFormatChange(format as QueryManagementFormat);
      handleSearchFormate({ searchKeyword });
    },
    [handleGetFormats, handleSelectedFormatChange]
  );

  const handleSelectedFolderChange = React.useCallback(
    (folder: FolderPrimaryListDto) => {
      setSelectedFolder(folder);
      handleGetFormats({
        searchKeyword,
        folder,
        context: FolderContext.inviteQueryFolder,
      });
      if (onSelectedFolderChange) {
        onSelectedFolderChange({
          searchKeyword,
          folder,
        });
      }
    },
    [handleGetFormats, searchKeyword, onSelectedFolderChange]
  );

  const closeFormatFolderCreatingDialog = React.useCallback(() => {
    setFormatFolderCreatingDialogOpened(false);
  }, []);

  const openFormatFolderCreatingDialog = React.useCallback(() => {
    setFormatFolderCreatingDialogOpened(true);
  }, []);

  const handleFormatFolderCreatingSubmit = async (
    params: CreateFormatFolderParams
  ) => {
    try {
      await createFormatFolder(params);
      closeFormatFolderCreatingDialog();
      showInfoMessage(I18n.t("Folder successfully created"));

      handleGetFolders();
    } catch (err) {
      console.error(err);

      const errorMessage =
        (err as HttpErrorDto)?.entity ||
        I18n.t("Failed to create format folder");
      showErrorMessage(errorMessage);
    } finally {
      setIsFormatsLoading(false);
    }
  };

  const handleFormatFolderDelete = () => {
    if (selectedFolder?.id) {
      //TODO: Note: BE should throw an error if the folder is not empty.
      showInfoMessage(
        I18n.t("Are you sure you want to delete the selected folder?"),
        {
          cancelText: I18n.t("No"),
          confirmText: I18n.t("Yes"),
          confirmBtnId: "btn-message-box-ok",
          cancelBtnId: "btn-message-box-dont-cancel",
          onCustomOk: async () => {
            try {
              await deleteFormatFolder({ folderId: selectedFolder.id });
              showInfoMessage(I18n.t("Folder successfully deleted"));

              setSelectedFolder(undefined);
              handleGetFolders();
            } catch (err) {
              console.error(err);

              const errorMessage =
                (err as HttpErrorDto)?.entity ||
                I18n.t("Failed to delete formats folder");
              showErrorMessage(errorMessage);
            }
          },
        }
      );
    }
  };

  const handleFormatDelete = () => {
    if (!isFormatIdEmpty) {
      showInfoMessage(
        I18n.t("Are you sure you want to delete the selected format?"),
        {
          cancelText: I18n.t("No"),
          confirmText: I18n.t("Yes"),
          confirmBtnId: "btn-message-box-ok",
          cancelBtnId: "btn-message-box-dont-cancel",
          onCustomOk: async () => {
            try {
              if (selectedFormat?.id) {
                await deleteFormat({
                  formatId: selectedFormat?.id,
                  context: FolderContext.inviteQueryFolder,
                });
                showInfoMessage(I18n.t("Format successfully deleted"));

                setSelectedFormat(undefined);
                handleGetFormats({ searchKeyword, folder: selectedFolder });
              }
            } catch (err) {
              console.error(err);

              const errorMessage =
                (err as HttpErrorDto)?.entity ||
                I18n.t("Failed to delete format");
              showErrorMessage(errorMessage);
            }
          },
        }
      );
    }
  };

  const handleChangeFormatDescription = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const format = {
        ...selectedFormat,
        description: e.target.value,
      } as QueryManagementFormat;
      handleSelectedFormatChange(format);
    },
    [selectedFormat, handleSelectedFormatChange]
  );

  return {
    folders,
    formats,
    isFoldersLoading,
    isFormatsLoading,
    selectedFolder,
    searchKeyword,
    selectedFormat,
    formatFolderCreatingDialogOpened,
    handleSearchChange,
    handleSelectedFolderChange,
    handleSelectedFormatChange,
    openFormatFolderCreatingDialog,
    closeFormatFolderCreatingDialog,
    handleFormatFolderCreatingSubmit,
    handleFormatFolderDelete,
    handleFormatDelete,
    handleChangeFormatDescription,
    isFormatIdEmpty,
    isFolderIdEmpty,
  };
};
